// ВЫБОР ЧИПОВ ПАНЕЛИ B (ШАГ 3) — арифметика потолка и свободной строки BG3. B5-4.
//
// ИСТОЧНИК ПОВЕДЕНИЯ — НЕ БАЙТ БОРДА, А ДВА РЕШЕНИЯ, И ЭТО ГЛАВНОЕ, ЧТО НАДО ЗНАТЬ ПРО ЭТОТ ФАЙЛ.
// Обработчик чипа борда (`Life Map - s3 Deck v8.html:550–555`) выглядит так:
//
//   if(at>=0) sel.splice(at,1);
//   else if(g.max===1) S.grp[S.i][g.id]=[o];
//   else { if(sel.length>=g.max) sel.shift(); sel.push(o) }
//
// Обе его половины разобраны в `s516`, и одна признана ДЕФЕКТОМ, а вторая — верной:
//   · `max: 1` — замена целиком ВЕРНА (`s516.d2`, часть 2): это обычный сингл-селект, и
//     запирание здесь сломало бы возможность передумать;
//   · `max > 1` — `sel.shift()` ДЕФЕКТ (`s516.d2`, часть 1): четвёртый тап в BG1 молча выбрасывает
//     первый выбор, человек видит те же три чипа, но уже не те. Чипы обязаны ЗАПИРАТЬСЯ.
//
// ⚠️ РАЗНИЦА МЕЖДУ ДВУМЯ СЛУЧАЯМИ НАМЕРЕННАЯ, И РЕШЕНИЕ ТРЕБУЕТ ДЕРЖАТЬ ИХ ВМЕСТЕ ДОСЛОВНО:
// «порознь эти две части читаются как непоследовательность, и следующий человек унифицирует их
// обратно». Поэтому они лежат в одной функции, а не в двух, и проверяются одной таблицей.
//
// ⛔ ВТОРОЕ ОТСТУПЛЕНИЕ ОТ БАЙТА — ЧИСТКА `S.cust[i]` (`s516.d1`). Чип и текст живут раздельно:
// сентинел `__cust` в выборе, текст в отдельном ряду; борд при снятии чипа убирает только
// сентинел, и адаптер (`toWizardInput`, ветка для импортов) дописывает осиротевший текст обратно
// как ответ. Замер живым адаптером: снял чип → ответ вернулся; снял и выбрал обычный вариант → в
// группу с `max: 1` приезжает ДВА ответа, второй — тот, от которого человек отказался. Выйти
// обычным путём нельзя: поле ввода видно только пока чип поднят. Решение прямо называет, кто
// чинит: ДЕКА, а не адаптер — ветка адаптера написана для импортов, где чипа не существует.
//
// ⛔ ЧТО НЕ ЗДЕСЬ. Ни `max`, ни списки опций тут не объявлены — они данные контракта
// (`BUTTON_GROUPS`, `s510.d2`). Вторая копия потолка разошлась бы с листом `?`, который печатает
// то же число строкой (`pickLine` в `deckCopy.js`).

import { BUTTON_GROUPS, CUSTOM_SENTINEL } from '../../contracts.js';

/** Выбор группы как массив строк. Не массив — пустой выбор: `undefined` доехал бы до конверта. */
const asPicked = (picked) => (Array.isArray(picked) ? picked : []);

/** Потолок группы из контракта. Нет группы — единица: строже, чем «сколько угодно». */
const capOf = (bg) => (BUTTON_GROUPS[bg] && BUTTON_GROUPS[bg].max) || 1;

/**
 * Счёт группы для строки подсказки (кадр `C3`: `<span class="cnt">2/2</span>`).
 *
 * `atCeiling` — не «полна ли группа», а «запирает ли она». У группы с потолком 1 выбор всегда
 * либо пуст, либо полон, и красить её счётчик амбером значило бы объявить запертой группу,
 * которая по решению не запирается никогда.
 *
 * @param {string[]} picked выбранное
 * @param {string} bg ключ группы контракта
 * @returns {{n: number, max: number, atCeiling: boolean}}
 */
export function groupCount(picked, bg) {
  const n = asPicked(picked).length;
  const max = capOf(bg);
  return { n, max, atCeiling: max > 1 && n >= max };
}

/**
 * Заперт ли ЭТОТ чип (кадр `C3`, `s516.d2` часть 1).
 *
 * Три условия, и каждое обязано стоять: потолок больше единицы (группа с одним ответом не
 * запирается вовсе), чип НЕ выбран (выбранный снимается всегда — он и есть выход) и группа стоит
 * на потолке.
 */
export function chipLocked(picked, bg, option) {
  const sel = asPicked(picked);
  const max = capOf(bg);
  return max > 1 && !sel.includes(option) && sel.length >= max;
}

/**
 * Один тап по чипу. Чистая функция: на входе выбор, на выходе новый выбор и два флага.
 *
 * `refused` — тап пришёл в запертый чип. Выбор не изменился, и вызывающий обязан ОТВЕТИТЬ
 * (кадр `C3·t`, «obligatory, not optional»): молчащий чип, который выглядит обычным, читается
 * как сломанный экран.
 *
 * `custCleared` — свободную строку BG3 надо стереть (`s516.d1`). Случая два, и второй легко
 * потерять: снятие самого сентинела И замена его обычным вариантом в группе с `max: 1`. Именно
 * второй давал в паспорте два ответа там, где ответ один.
 *
 * @param {string[]} picked выбранное в группе
 * @param {string} bg ключ группы контракта
 * @param {string} option опция или `__cust`
 * @returns {{picked: string[], refused: boolean, custCleared: boolean}}
 */
export function toggleChip(picked, bg, option) {
  const sel = asPicked(picked);
  const max = capOf(bg);
  const at = sel.indexOf(option);

  // СНЯТИЕ — всегда законно, потолок ему не помеха: он и есть путь из-под потолка.
  if (at >= 0) {
    return {
      picked: sel.filter((_, i) => i !== at),
      refused: false,
      custCleared: option === CUSTOM_SENTINEL,
    };
  }

  // ЗАМЕНА при потолке 1 (`s516.d2` часть 2 — верное поведение борда, переносится как есть).
  if (max === 1) {
    return {
      picked: [option],
      refused: false,
      custCleared: sel.includes(CUSTOM_SENTINEL) && option !== CUSTOM_SENTINEL,
    };
  }

  // ЗАПОР при потолке выше 1 (`s516.d2` часть 1 — вместо `sel.shift()` борда).
  if (sel.length >= max) {
    return { picked: sel, refused: true, custCleared: false };
  }

  return { picked: [...sel, option], refused: false, custCleared: false };
}

/**
 * Поднят ли чип свободной строки — то есть рисуется ли поле ввода под чипами.
 *
 * Отдельным предикатом, а не `includes` по месту: поле ввода и чип обязаны появляться и исчезать
 * ОДНИМ условием. Разъедутся — и получится ровно то состояние `s516.d1`, где текст жив, а поля
 * под него на экране нет.
 */
export const customOpen = (picked) => asPicked(picked).includes(CUSTOM_SENTINEL);

/** Есть ли у группы свободная строка вовсе (контракт: только у BG3). */
export const hasCustom = (bg) => !!(BUTTON_GROUPS[bg] && BUTTON_GROUPS[bg].custom);
